import { useState, useEffect } from 'react';
import { useOrganizationSettings } from './useOrganizationSettings';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Consistent hex to HSL conversion function
const hexToHsl = (hex: string): string => {
  if (!hex || !hex.startsWith('#')) return '';
  
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h: number, s: number, l: number;

  l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0;
    }
    h /= 6;
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

// Apply CSS variable to document root
const applyCssVariable = (variableName: string, value: string) => {
  if (!value) return;
  document.documentElement.style.setProperty(variableName, value);
};

export const useThemeManager = (explicitOrgId?: string) => {
  // Global theme state
  const [globalPrimaryColor, setGlobalPrimaryColor] = useState<string>('#3dc298');
  const [globalThemeLoading, setGlobalThemeLoading] = useState(true);
  
  // Organization theme hooks
  const { getSetting, loading: orgSettingsLoading, updateSetting } = useOrganizationSettings(explicitOrgId);
  
  // Load global theme on init
  useEffect(() => {
    const fetchGlobalTheme = async () => {
      try {
        const { data, error } = await supabase
          .from('app_settings')
          .select('value')
          .eq('key', 'theme_primary_color')
          .maybeSingle();

        if (error) {
          console.error('Error fetching global theme color:', error);
          return;
        }

        if (data?.value) {
          setGlobalPrimaryColor(data.value);
        }
      } catch (error) {
        console.error('Error fetching global theme color:', error);
      } finally {
        setGlobalThemeLoading(false);
      }
    };

    fetchGlobalTheme();
  }, []);
  
  // Apply themes when settings change
  useEffect(() => {
    if (orgSettingsLoading || globalThemeLoading) return;
    
    // First check for organization theme settings
    const primaryColor = getSetting('theme_primary_color');
    const secondaryColor = getSetting('theme_secondary_color');
    const accentColor = getSetting('theme_accent_color');
    const warningColor = getSetting('theme_warning_color');
    const errorColor = getSetting('theme_error_color');
    
    // Apply organization settings if they exist
    if (primaryColor) {
      applyCssVariable('--primary', hexToHsl(primaryColor));
    } else if (globalPrimaryColor) {
      // Fall back to global theme
      applyCssVariable('--primary', hexToHsl(globalPrimaryColor));
    }
    
    if (secondaryColor) applyCssVariable('--secondary', hexToHsl(secondaryColor));
    if (accentColor) applyCssVariable('--accent', hexToHsl(accentColor));
    if (warningColor) applyCssVariable('--warning', hexToHsl(warningColor));
    if (errorColor) applyCssVariable('--destructive', hexToHsl(errorColor));
    
    // Apply layout preferences
    const borderRadius = getSetting('theme_border_radius');
    const fontScale = getSetting('theme_font_scale');
    
    if (borderRadius) {
      const radiusMap: Record<string, string> = {
        'none': '0',
        'small': '0.375rem',
        'medium': '0.75rem',
        'large': '1rem',
        'full': '9999px'
      };
      applyCssVariable('--radius', radiusMap[borderRadius] || '0.75rem');
    }
    
    if (fontScale) {
      const scaleMap: Record<string, string> = {
        'compact': '0.9',
        'normal': '1',
        'comfortable': '1.1'
      };
      applyCssVariable('--font-scale', scaleMap[fontScale] || '1');
    }
  }, [getSetting, orgSettingsLoading, globalPrimaryColor, globalThemeLoading]);
  
  // Update global theme
  const setGlobalTheme = async (color: string) => {
    try {
      // Update in database using admin function
      const { error } = await supabase.rpc(
        'admin_update_app_setting',
        {
          p_key: 'theme_primary_color',
          p_value: color,
          p_description: 'Global primary theme color for all users'
        }
      );

      if (error) {
        throw error;
      }

      // Update local state
      setGlobalPrimaryColor(color);
      applyCssVariable('--primary', hexToHsl(color));
      
      toast.success('Theme color updated globally');
    } catch (error: any) {
      console.error('Error updating global theme color:', error);
      toast.error('Failed to update theme color: ' + error.message);
    }
  };
  
  // Update organization theme
  const setOrganizationTheme = async (key: string, value: string) => {
    await updateSetting(key, value);
    
    // Apply immediately
    if (key.startsWith('theme_') && key.endsWith('_color')) {
      const cssVarName = `--${key.replace('theme_', '').replace('_color', '')}`;
      applyCssVariable(cssVarName, hexToHsl(value));
    }
  };

  return {
    // Global theme
    globalPrimaryColor,
    setGlobalTheme,
    globalThemeLoading,
    
    // Organization theme
    setOrganizationTheme,
    orgSettingsLoading,
    getSetting,
    
    // Utils
    hexToHsl
  };
};